import Vue from "vue";
import liveObjectsService from "@/plugins/liveObjects";
import DashboardService from "@/services/dashboard-service";

const modifierKey = (productId, modifierOptionId) =>
  `pm:${productId}:${modifierOptionId}`;

const state = () => ({
  /** @type {Record<string, true>} product id string -> whole product 86'd */
  productIds: {},
  /** @type {Record<string, true>} key pm:productId:modifierOptionId -> modifier 86'd for that product */
  modifierKeys: {},
  /** @type {Record<string, number>} product id string -> timestamp (ms) of most recent history message applied */
  historyTimestamps: {},
});

const getters = {
  productIds86d: (state) => state.productIds,
  modifierKeys86d: (state) => state.modifierKeys,
  isProduct86d: (state) => {
    const ids = state.productIds;
    return (id) => !!ids[String(id)];
  },
  isModifier86d: (state) => {
    const keys = state.modifierKeys;
    return (productId, modifierOptionId) =>
      !!keys[modifierKey(productId, modifierOptionId)];
  }
};

const mutations = {
  setProduct86d (state, { productId, value }) {
    const id = String(productId);
    if (value) {
      Vue.set(state.productIds, id, true);
    } else {
      Vue.delete(state.productIds, id);
    }
  },
  setModifier86d (state, { productId, modifierOptionId, value }) {
    const k = modifierKey(productId, modifierOptionId);
    if (value) {
      Vue.set(state.modifierKeys, k, true);
    } else {
      Vue.delete(state.modifierKeys, k);
    }
  },
  replaceAll (state, { productIds, modifierKeys }) {
    state.productIds = productIds ? { ...productIds } : {};
    state.modifierKeys = modifierKeys ? { ...modifierKeys } : {};
  },
  setHistoryTimestamp (state, { productId, ts }) {
    Vue.set(state.historyTimestamps, String(productId), ts);
  },
  replaceProductIds (state, productIds) {
    state.productIds = productIds ? { ...productIds } : {};
  },
  replaceModifierKeys (state, modifierKeys) {
    state.modifierKeys = modifierKeys ? { ...modifierKeys } : {};
  }
};

const actions = {
  hydrateFromProducts ({ commit }, products) {
    const productIds = {};
    const modifierKeys = {};
    (Array.isArray(products) ? products : []).forEach((product) => {
      if (product && product.is_86d) {
        productIds[String(product.id)] = true;
      }
      (product?.modifier_groups || []).forEach((group) => {
        (group?.modifiers || []).forEach((modifier) => {
          if (modifier?.is_86d) {
            modifierKeys[modifierKey(product.id, modifier.id)] = true;
          }
        });
      });
    });
    commit("replaceProductIds", productIds);
    commit("replaceModifierKeys", modifierKeys);
  },

  applyRealtime ({ commit }, payload) {
    if (!payload) return;
    const pid = payload.product_id;
    if (pid == null) return;
    const mid = payload.modifier_option_id;
    if (mid != null && mid !== "") {
      commit("setModifier86d", {
        productId: pid,
        modifierOptionId: mid,
        value: !!payload.is_86d
      });
    } else {
      commit("setProduct86d", { productId: pid, value: !!payload.is_86d });
    }
  },

  async setAndPublish ({ commit }, { productId, is86d }) {
    const availability = await DashboardService.updateProductAvailability(productId, {
      manual_is_86d: is86d
    });
    const effective86d = availability && typeof availability.is_86d !== "undefined"
      ? availability.is_86d
      : is86d;
    commit("setProduct86d", { productId, value: effective86d });
    if (!liveObjectsService.isReady()) return;
    liveObjectsService.publishProduct86d({
      product_id: productId,
      is_86d: effective86d,
      manual_is_86d: availability?.manual_is_86d ?? is86d,
      auto_is_86d: availability?.auto_is_86d ?? false,
      source: liveObjectsService.getClientId(),
      updated_at: Date.now()
    });
  },

  async setModifierAndPublish ({ commit }, { productId, modifierOptionId, is86d }) {
    const availability = await DashboardService.updateProductModifierAvailability(
      productId,
      modifierOptionId,
      { manual_is_86d: is86d }
    );
    const effective86d = availability && typeof availability.is_86d !== "undefined"
      ? availability.is_86d
      : is86d;
    commit("setModifier86d", { productId, modifierOptionId, value: effective86d });
    if (!liveObjectsService.isReady()) return;
    liveObjectsService.publishProduct86d({
      product_id: productId,
      modifier_option_id: modifierOptionId,
      is_86d: effective86d,
      manual_is_86d: availability?.manual_is_86d ?? is86d,
      source: liveObjectsService.getClientId(),
      updated_at: Date.now()
    });
  },

  async reconcileFromHistory ({ commit }) {
    console.log('[products86d] reconcileFromHistory started');
    try {
      const messages = await liveObjectsService.getMissedProduct86dMessages();
      console.log('[products86d] reconcileFromHistory got', messages.length, 'messages', messages.map(m => ({ ...m.data, ts: m.timestamp })));
      for (const msg of messages) {
        const data = msg.data;
        if (!data || data.product_id == null) continue;
        console.log('[products86d] reconcileFromHistory applying product_id=', data.product_id, 'is_86d=', data.is_86d, 'ts=', msg.timestamp);
        commit('setProduct86d', { productId: data.product_id, value: !!data.is_86d });
        // Record the timestamp so stale live messages arriving after this don't override
        if (msg.timestamp) commit('setHistoryTimestamp', { productId: data.product_id, ts: msg.timestamp });
        liveObjectsService.publishProduct86d(data).catch(() => {});
      }
    } catch (e) {
      console.warn('[products86d] reconcileFromHistory failed', e);
    }
  },

  applyRealtimeIfNewer ({ state, commit }, { payload, timestamp }) {
    if (!payload) return;
    const pid = payload.product_id;
    if (pid == null) return;
    const historyTs = state.historyTimestamps[String(pid)] || 0;
    if (historyTs > 0 && timestamp && timestamp <= historyTs) {
      console.log('[products86d] ignoring stale live message product_id=', pid, 'ts=', timestamp, '<=', historyTs);
      return;
    }
    const mid = payload.modifier_option_id;
    if (mid != null && mid !== '') {
      commit('setModifier86d', { productId: pid, modifierOptionId: mid, value: !!payload.is_86d });
    } else {
      commit('setProduct86d', { productId: pid, value: !!payload.is_86d });
    }
  },

  async reconcileFromSnapshot ({ state, commit }) {
    if (!liveObjectsService.isReady()) return;
    try {
      const rows = await liveObjectsService.getAllProduct86dSnapshot();
      const productIds = {};
      const modifierKeys = {};
      rows.forEach((row) => {
        if (!row || !row.is_86d) return;
        const pid = row.product_id;
        if (pid == null) return;
        if (row.modifier_option_id != null && row.modifier_option_id !== "") {
          modifierKeys[modifierKey(pid, row.modifier_option_id)] = true;
        } else {
          productIds[String(pid)] = true;
        }
      });
      commit("replaceProductIds", { ...state.productIds, ...productIds });
      commit("replaceModifierKeys", modifierKeys);
    } catch (e) {
      console.warn("[products86d] reconcile failed", e);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
